/**
 * The vocabulary of an affiliation claim: somebody saying who they are to, and
 * the other party confirming or not.
 *
 * These values must stay in step with the enums in
 * supabase/migrations/20260808090100_affiliation_claims.sql. Nothing enforces
 * that but this comment — the same standing arrangement as `domain/provenance.ts`.
 *
 * READ THIS NEXT TO `domain/membership.ts`, because the two look alike and are
 * not. A `Membership` is a GRANT: it is what `deriveWorkspaces()` reads, it is
 * what Row Level Security reads, and only ONDO can issue one. A claim is a
 * STATEMENT, and confirming one writes no membership, opens no workspace, and
 * returns no row that was withheld before. If that ever stops being true it is a
 * migration with its own argument, not a quiet change here.
 */
/**
 * What kind of relationship is being claimed.
 *
 * None of these is a job title — `title` on the claim is free text for that,
 * exactly as `Membership.title` is. This is the part a policy could read if one
 * ever needed to, and today none does.
 */
export const AFFILIATION_KINDS = [
    "employment",
    "representation",
    "directorship",
    "advisory",
    "membership",
    "other",
];
/**
 * How a claim ended.
 *
 * `confirmed` and `disputed` are the two real answers. The rest are here for the
 * reasons they are on a verification request: a party may refuse to engage, may
 * genuinely not remember, may have the claim taken back, or may never answer.
 */
export const AFFILIATION_OUTCOMES = [
    "confirmed",
    "disputed",
    "declined",
    "no_recall",
    "withdrawn",
    "expired",
];
/**
 * The four ways to answer one. The same shape verification requests take, and
 * for the same reason — one inbox should not need two mental models.
 */
export const AFFILIATION_ANSWERS = [
    "confirm",
    "dispute",
    "no_recall",
    "decline",
];
/** Still waiting on the party it is about. */
export function isLiveClaim(outcome) {
    return outcome === null;
}
/**
 * The other party answered.
 *
 * Unlike its provenance counterpart this grants nothing and therefore says
 * nothing about a read surviving. It is only ever a filter on a screen.
 */
export function wasClaimAnswered(outcome) {
    return (outcome === "confirmed" ||
        outcome === "disputed" ||
        outcome === "declined" ||
        outcome === "no_recall");
}
/**
 * Who a party is willing to have see one of its relationships.
 *
 * COLUMNS AND NOT A POSTGRES ENUM, which is the one thing in this file with no
 * enum behind it. `ondo.affiliation_publications` carries three booleans, and
 * that was decision 2 of
 * `supabase/migrations/20261005095000_published_affiliations.sql`: the product
 * default is "every audience enabled", which a ladder could only express as
 * whichever rung happened to be widest.
 *
 * NONE OF THESE IS A PUBLIC SURFACE. `everyone` is a ceiling the party set, not
 * a door to the party — every reader of a published relationship is
 * authenticated and already holds the party's ONDO id, and `anon` reaches
 * nothing in the `ondo` schema. Decision 10, and `accessFGARules.md` on why a
 * second public surface is not something a checkbox may create.
 *
 * Ordered widest to narrowest, which is how they nest and how a screen should
 * offer them.
 */
export const AFFILIATION_AUDIENCES = ["everyone", "ondo_users", "dealing_parties"];
/**
 * The default: every audience.
 *
 * In the database this is the ABSENCE of a publication row rather than a row
 * full of `true`, so there is one definition of it and it lives in the read.
 * See decision 3 of the migration for why it is not a backfill.
 */
export const ALL_AFFILIATION_AUDIENCES = {
    everyone: true,
    ondo_users: true,
    dealing_parties: true,
};
/** Nothing published — how a relationship is hidden without being deleted. */
export const NO_AFFILIATION_AUDIENCE = {
    everyone: false,
    ondo_users: false,
    dealing_parties: false,
};
/** A wider audience implies the narrower ones. Mirrors `publish_affiliation`. */
export function normaliseAudiences(chosen) {
    const everyone = chosen.everyone;
    const ondoUsers = everyone || chosen.ondo_users;
    return {
        everyone,
        ondo_users: ondoUsers,
        dealing_parties: ondoUsers || chosen.dealing_parties,
    };
}
/** Whether any audience at all can see it. */
export function isPublishedToAnyone(audiences) {
    return audiences.everyone || audiences.ondo_users || audiences.dealing_parties;
}
export function affiliationStanding(outcome) {
    switch (outcome) {
        case "confirmed":
            return "confirmed";
        case "disputed":
            return "disputed";
        case "no_recall":
            return "not_recalled";
        case "declined":
            return "declined";
        case "withdrawn":
            return "withdrawn";
        case "expired":
            return "expired";
        default:
            return "unanswered";
    }
}
/**
 * Whether the record stands behind the relationship without qualification.
 *
 * True for exactly one standing. Anything a screen draws as a plain
 * confirmation has to pass this — and the bug should be hard to write: there is
 * no `isAnswered`-shaped function beside it that a careless call site could
 * mistake for it. `wasClaimAnswered` above is about the lifecycle and says so,
 * and it is true of a dispute where this is false.
 */
export function standsConfirmed(outcome) {
    return affiliationStanding(outcome) === "confirmed";
}
/**
 * Whether the answer must be shown beside the claim wherever it is published.
 *
 * OND-98 decision 3: "the dispute and its answer must appear alongside the
 * claim". A dispute and a don't-recall both qualify, and so does a declined
 * answer — "we do not comment on this" is a thing the other party said.
 */
export function needsItsAnswerShown(outcome) {
    const standing = affiliationStanding(outcome);
    return standing === "disputed" || standing === "not_recalled" || standing === "declined";
}
//# sourceMappingURL=affiliations.js.map